import { ReactNode, useState } from "react";
import { AppSidebar } from "@/components/AppSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileNavBar } from "@/components/dashboard/MobileNavBar";
import { MobileSidebarContent } from "@/components/dashboard/MobileSidebarContent";
import { MobileBottomNav } from "@/components/mobile/MobileBottomNav";
import { useGestureNavigation } from "@/hooks/useGestureNavigation";
import { OfflineIndicator } from "@/components/mobile/OfflineIndicator";
import { useIOSSafeArea } from "@/hooks/useIOSOptimizations";
import { OnboardingWizard } from "@/components/onboarding/OnboardingWizard";
import { useProfileSetup } from "@/hooks/useProfileSetup";
import { useRoutePrefetch } from "@/hooks/useRoutePrefetch";
import { useRealtimeDashboard } from "@/hooks/useRealtimeDashboard";
import { Loader2 } from "lucide-react";
import { InstallPrompt } from "@/components/mobile/InstallPrompt";
import { PageTransition } from "@/components/layouts/PageTransition";
import { FloatingChatBubble } from "@/components/ai-hub/FloatingChatBubble";
import { TourPrompt } from "@/components/onboarding/TourPrompt";

interface DashboardLayoutProps {
  children: ReactNode;
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const isMobile = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { needsSetup, loading, refetch } = useProfileSetup();
  
  // Enable route prefetching for faster navigation
  useRoutePrefetch();
  
  // Enable realtime updates for dashboard data
  useRealtimeDashboard();
  
  // Enable swipe-back gesture navigation on mobile
  useGestureNavigation({ enabled: isMobile });
  
  // iOS-specific optimizations
  useIOSSafeArea();

  // Show loading state while checking profile
  if (loading) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Show onboarding wizard if company name is not set
  if (needsSetup) {
    return (
      <OnboardingWizard
        open={true}
        onComplete={async () => {
          await refetch();
        }}
      />
    );
  }
  
  if (isMobile) {
    return (
      <div className="flex min-h-screen w-full bg-background flex-col">
        {/* Offline Indicator */}
        <OfflineIndicator />

        {/* Mobile Navigation Bar */}
        <MobileNavBar onMenuClick={() => setMobileMenuOpen(true)} />

        {/* Mobile Sidebar Drawer */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetContent side="left" className="p-0 w-[280px] [&>button]:hidden">
            <MobileSidebarContent onClose={() => setMobileMenuOpen(false)} />
          </SheetContent>
        </Sheet>
        
        <main id="main-content" className="flex-1 flex flex-col pb-16">
          <PageTransition>
            {children}
          </PageTransition>
        </main>

        {/* Install Prompt for PWA */}
        <InstallPrompt />

        {/* Floating AI Chat Bubble */}
        <FloatingChatBubble />

        {/* Tour Prompt for new users */}
        <TourPrompt />

        {/* Mobile Bottom Navigation */}
        <MobileBottomNav />
      </div>
    );
  }
  
  return (
    <SidebarProvider defaultOpen={true}>
      <div className="flex min-h-screen w-full bg-background">
        <AppSidebar />
        
        <div className="flex-1 flex flex-col">
          <main id="main-content" className="flex-1">
            <PageTransition>
              {children}
            </PageTransition>
          </main>
        </div>

        {/* Floating AI Chat Bubble */}
        <FloatingChatBubble />

        {/* Tour Prompt for new users */}
        <TourPrompt />
      </div>
    </SidebarProvider>
  );
}
